import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { startOrderSchema } from '../../Utils/yupvalidations';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  coverageDetails,
  generateTimeStamp,
  generateTimeStampOffset,
} from '../../Utils/helperFns';
import { useAppSelector } from '../../redux/store';

import { useDispatch } from 'react-redux';
import {
  addToCart,
  clearFields,
  setQuantity,
  setShifts,
} from '../../redux/orderSlice';
import {
  getPreviewShiftDetails,
  setPreviewShiftDetails,
  setStartOrderData,
} from '../../redux/commonSlice';
import {
  getPackageDetailPage,
  getProductDetails,
  getServiceDetailPage,
} from '../../redux/storeSlice';

import { PRODUCT_TYPES, RENTAL_SERVICE_TYPE } from '../../Utils/constantData';

import OrderForm from './components/OrderForm';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { notify } from '../../Utils/toastify';
import { ROUTES } from '../../helpers/contants';
import { useAnalytics } from '../../hooks/useAnalytics';
import { ANALYTICS_EVENTS } from '../../helpers/analyticsEvents';

const StartOrder = () => {
  const { productDetails, packageDetails, loading, serviceDetails } =
    useAppSelector(state => state.store);
  const navigate = useNavigate();
  const { quantity, shifts } = useAppSelector(state => state.order);
  const preview = useAppSelector(state => state.common.previewShiftDetails);
  const [apiLoading, setApiLoading] = useState(false);
  const [hasChangedAnyValue, setHasChangedAnyValue] = useState(false);
  const { track } = useAnalytics();
  //useForm
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    setError,
    watch,
    getValues,
    trigger,
    reset,
    register,
  } = useForm({
    resolver: yupResolver(startOrderSchema),
    mode: 'all',
  });

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const type = searchParams.get('type');
  const rentIndex = searchParams.get('rentIndex');
  const { state } = useLocation();
  const perviewShiftDetails = useAppSelector(
    state => state.common.previewShiftDetails,
  );
  const dispatch = useDispatch();
  useEffect(() => {
    if (id) {
      // get Service details if type includes SERVICE and only if not prefetched
      if (type) setValue('shoppingMode', type);
      if (type?.includes(`${PRODUCT_TYPES.SERVICE}`)) {
        getServiceDetailPage(id);
      }
      //  otherwise get PRODUCT details if type includes PRODUCT
      else if (type?.includes(`${PRODUCT_TYPES.PRODUCT}`)) {
        getProductDetails(id);
      }
      //  otherwise get PACKAGE details if type includes PACKAGE
      else if (type?.includes(`${PRODUCT_TYPES.PACKAGE}`)) {
        getPackageDetailPage(id);
      }
    }
  }, [id, type]);

  const onSubmit = (data: any, submitType: string) => {
    // common payload for all types
    setApiLoading(true);
    console.log('submitType', submitType);

    const commonPayload = {
      qty: quantity,

      productId: undefined,
      address: data.Location?.addressLine1,
      branchId: '',
      productName: undefined,
      shoppingMode: '', // will set this below
      longitude: data.Location?.location?.coordinates[0],
      latitude: data.Location?.location?.coordinates[1],
      addressType: data.Location?.addressType,
      country: data.Location?.country,
      postalCode: data.Location?.postalCode,
      locationsStreet: data.Location?.locationsStreet,
      attachmentUrls:
        data?.Attachments?.map((item: any) => ({
          name: item?.docName,
          url: item?.url,
        })) || [],
      setId: data.Location?.setId,
      setName: data.Location?.setName,
      locationId: data.Location?._id,
      locationName: data.Location?.locationName,
      productionId: data.Location?.productionId,
      departmentId: data.Department._id,
      deptName: data.Department.deptName,
      setScheduleType: data?.['Set Schedule'],
      productionScheduleType: data?.['Production Schedule'],
      productionName: data.Location?.productionName,
      productionType: data.Location?.productionType,
      note: data?.Notes,
      shiftCoverage: 'Swing Shift',
      ...(data.Location?.episodeName && {
        movie: data.Location.episodeName,
      }),
      ...(data.Location?.episodeName && {
        episodeName: data.Location.episodeName,
      }),
      ...(data.Location?.episodeId && {
        episodeId: data.Location.episodeId,
      }),
      ...(data.Location?.episodeNumber && {
        episodeNumber: data.Location.episodeNumber.toString(),
      }),
    };

    let payload;

    switch (type) {
      // if servive rented
      case `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}`:
        payload = {
          ...commonPayload,
          branchId: serviceDetails?.branchId,
          productId: serviceDetails?._id,
          productName: serviceDetails?.title,
          rentPriceId: serviceDetails?.rentPricing?.[rentIndex!]?._id,
          shoppingMode: `${RENTAL_SERVICE_TYPE.SERVICE}`,
        };
        break;

      // if product is rented
      case `${PRODUCT_TYPES.PRODUCT}-${RENTAL_SERVICE_TYPE.RENT}`:
        payload = {
          ...commonPayload,
          branchId: productDetails?.branchId,
          productId: productDetails?._id,
          productName: productDetails?.title,
          rentPriceId: productDetails?.rentPricing?.[rentIndex!]?._id,
          shoppingMode: `${RENTAL_SERVICE_TYPE.RENT}`,
        };
        break;

      // if package is rented
      case `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}`:
        payload = {
          ...commonPayload,
          branchId: packageDetails?.branchId,
          productId: packageDetails?._id,
          productName: packageDetails?.title,
          isPackage: true,
          rentPriceId: packageDetails?.rentPricing?.[rentIndex!]?._id,

          shoppingMode:
            packageDetails?.rentPricing?.[rentIndex!]?.priceMode ==
            RENTAL_SERVICE_TYPE.PER_HOUR
              ? `${RENTAL_SERVICE_TYPE.SERVICE}`
              : `${RENTAL_SERVICE_TYPE.RENT}`,
        };
        break;

      // if product buy
      case `${PRODUCT_TYPES.PRODUCT}-${RENTAL_SERVICE_TYPE.BUY}`:
        payload = {
          ...commonPayload,
          branchId: productDetails?.branchId,
          productId: productDetails?._id,
          productName: productDetails?.title,
          shoppingMode: `${RENTAL_SERVICE_TYPE.BUY}`,
        };
        break;

      // if package buy
      case `${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.BUY}`:
        payload = {
          ...commonPayload,
          branchId: packageDetails?.branchId,
          productId: packageDetails?._id,
          productName: packageDetails?.title,
          shoppingMode: `${RENTAL_SERVICE_TYPE.BUY}`,
          isPackage: true,
        };
        break;

      default:
        payload = {
          ...commonPayload,
          shoppingMode: `${RENTAL_SERVICE_TYPE.BUY}`,
        };
        break;
    }

    // if service rented or package rented then send shift coverage only for hour price model
    if (
      (`${PRODUCT_TYPES.PACKAGE}-${RENTAL_SERVICE_TYPE.RENT}` == type &&
        packageDetails?.rentPricing?.[rentIndex!]?.priceMode ==
          RENTAL_SERVICE_TYPE.PER_HOUR) ||
      `${PRODUCT_TYPES.SERVICE}-${RENTAL_SERVICE_TYPE.RENT}` == type
    ) {
      const rentData = type?.includes(`${PRODUCT_TYPES.SERVICE}`)
        ? serviceDetails
        : type?.includes(`${PRODUCT_TYPES.PACKAGE}`)
        ? packageDetails
        : productDetails;

      payload = {
        ...payload,
        shiftCoverage: rentData?.rentPricing?.[rentIndex!]?.isFlexibleShiftHr
          ? 'Flexible'
          : data?.Coverage == 'autoCoverage'
          ? coverageDetails(
              getValues('Start Time'),
              rentData?.rentPricing?.[rentIndex!]?.shiftLength,
            )?.includes('Day')
            ? 'Day'
            : 'Night'
          : data?.Coverage == '24 Hrs Shift'
          ? '24 Hrs'
          : data?.Coverage == 'Specific Duration'
          ? 'Swing Shift'
          : data?.Coverage,
      };
    }
    if (type?.includes(`${RENTAL_SERVICE_TYPE.RENT}`)) {
      payload = {
        ...payload,
        startTime: generateTimeStamp(
          getValues('Start Date'),
          getValues('Start Time'),
        ),
        endTime: generateTimeStamp(
          getValues('End Date'),
          getValues('End Time'),
        ),
        startOffset: generateTimeStampOffset(
          data?.['Start Date'],
          data?.['Start Time'],
        ),
        endOffset: generateTimeStampOffset(
          data?.['End Date'],
          data?.['End Time'],
        ),
      };
    }
    if (perviewShiftDetails.shiftTimes && submitType !== 'preview') {
      payload['isTempCartItem'] = true;
    }
    if (submitType == 'preview') {
      payload['cartType'] = 'TEMP_CART';
    }
    if (!preview.shiftTimes || submitType !== 'preview' || hasChangedAnyValue) {
      addToCart(
        payload,
        (resData: any) => {
          setApiLoading(false);
          if (submitType === 'preview') {
            const cartResopnse = {
              ...resData?.data,
              shiftTimes: {
                fromDate: dayjs(data?.['Start Date']).valueOf(),
                toDate: dayjs(data?.['End Date']).valueOf(),
                setName: data.Location?.setName,
                episodeName: data.Location?.episodeName,
                episodeNumber: data.Location?.episodeNumber,
              },
            };
            getPreviewShiftDetails(cartResopnse);
          } else {
            notify('Added to cart', 'success');
            track(ANALYTICS_EVENTS.ADD_TO_CART, {
              product_id: id ?? undefined,
              product_name:
                productDetails?.title ||
                packageDetails?.title ||
                serviceDetails?.title,
              product_type: type ?? undefined,
            });
          }

          if (submitType !== 'preview') {
            navigate('/cart');
            dispatch(setStartOrderData({}));
            dispatch(setShifts(''));
            dispatch(setQuantity(''));
            dispatch(setPreviewShiftDetails({}));
          } else {
            navigate('/preview-shift', {
              state: {
                ...state,
                url: location.pathname + location.search,
              },
            });
            track(ANALYTICS_EVENTS.PREVIEW_SHIFT, {
              product_id: id ?? undefined,
              product_name:
                productDetails?.title ||
                packageDetails?.title ||
                serviceDetails?.title,
            });
            dispatch(
              setStartOrderData({
                startTime: dayjs(getValues('Start Time')),
                endTime: dayjs(getValues('End Time')),
                startDate: dayjs(getValues('Start Date')),
                endDate: dayjs(getValues('End Date')),
                shiftCoverage: data?.Coverage,
                attachments: data?.Attachments,
                notes: data?.Notes,
                location: data?.Location,
                production: {
                  name: data?.Location.productionName,
                  _id: data?.Location.productionId,
                },
                productionScheduleType: data?.['Production Schedule'],
                setScheduleType: data?.['Set Schedule'],
                departMent: data?.Department,
                quantity: quantity,
                shifts: shifts,
              }),
            );
          }
        },
        () => setApiLoading(false),
      );
    } else {
      navigate('/preview-shift', {
        state: {
          ...state,
          url: location.pathname + location.search,
        },
      });
      track(ANALYTICS_EVENTS.PREVIEW_SHIFT, {
        product_id: id ?? undefined,
        product_name:
          productDetails?.title ||
          packageDetails?.title ||
          serviceDetails?.title,
      });
      dispatch(
        setStartOrderData({
          startTime: dayjs(getValues('Start Time')),
          endTime: dayjs(getValues('End Time')),
          startDate: dayjs(getValues('Start Date')),
          endDate: dayjs(getValues('End Date')),
          shiftCoverage: data?.Coverage,
          attachments: data?.Attachments,
          notes: data?.Notes,
          location: data?.Location,
          production: {
            name: data?.Location.productionName,
            _id: data?.Location.productionId,
          },
          productionScheduleType: data?.['Production Schedule'],
          setScheduleType: data?.['Set Schedule'],
          departMent: data?.Department,
          quantity: quantity,
          shifts: shifts,
        }),
      );
    }
    clearFields();
  };

  // call lastShift hours when form is valid

  // prepopulate DepartMent First value
  // "cartType": "TEMP_CART",

  // closing address window

  return (
    <>
      <section className="start-order ">
        <div className="title-flex mb-24">
          <div>
            <div className="d-flex align-center mb-8">
              <button
                className="blank-btn pl0 mr2"
                onClick={() =>
                  navigate(
                    `/store-${type?.split(`-`)[0]?.toLowerCase()}-details/${
                      state.itemId
                    }`,
                    { state: state },
                  )
                }
              >
                <i className="back-icon"></i>
              </button>
              <h1 className=" MuiTypography-h3 fw600 ">Start an Order</h1>
            </div>
            <ul className="breadCrumbs">
              {state?.from == 'store' ? (
                <>
                  <li onClick={() => navigate('/store')}>Store</li>
                  <li
                    onClick={() =>
                      navigate(
                        `/store-${type?.split(`-`)[0]?.toLowerCase()}-details/${
                          state?.itemId
                        }`,
                        { state: state },
                      )
                    }
                  >
                    {state?.itemName}
                  </li>

                  <li>Start an Order</li>
                </>
              ) : (
                <>
                  <li>
                    <Link to="/store">Store</Link>
                  </li>
                  <li
                    onClick={() =>
                      navigate(`${ROUTES.VENDORDETAILS}/${state?.vendorId}`, {
                        state: state,
                      })
                    }
                  >
                    {state?.vendorName}
                  </li>
                  <li
                    onClick={() =>
                      navigate(
                        `/store-${type?.split(`-`)[0]?.toLowerCase()}-details/${
                          state?.itemId
                        }`,
                        { state: state },
                      )
                    }
                  >
                    {state?.itemName}
                  </li>
                  <li>Start an Order</li>
                </>
              )}
            </ul>
          </div>
        </div>
        <OrderForm
          setValue={setValue}
          control={control}
          onSubmit={onSubmit}
          setError={setError}
          handleSubmit={handleSubmit}
          rentIndex={rentIndex!}
          errors={errors}
          register={register}
          trigger={trigger}
          getValues={getValues}
          watch={watch}
          type={type!}
          reset={reset}
          isValid={isValid}
          data={
            type?.includes(`${PRODUCT_TYPES.SERVICE}`)
              ? serviceDetails
              : type?.includes(`${PRODUCT_TYPES.PACKAGE}`)
              ? packageDetails
              : productDetails
          }
          apiLoading={apiLoading}
          setHasChangedAnyValue={setHasChangedAnyValue}
        />
      </section>
    </>
  );
};

export default StartOrder;
